import { Component, OnInit, OnDestroy } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import {
  TbasService,
  SessionService,
  UtilsService,
  NotificationService,
  NumberFormatService
} from '../../../shared/services';
import { ModalService, ClosableModal } from '../../../shared/modules/modal';
import { TbaConstants } from '../../../shared/constants/tba';

import { ConfirmationModalComponent } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { MessageModalComponent } from '../../../shared/components/message/message-modal.component';

import { PaginationConstants,
  // TranslationConstants
} from '../../../shared/constants';
import {
  TbaParameterAmountEnum,
  TransactionTypeEnum,
  TbaEvaluationMethodEnum
} from '../../../shared/models/enums';

import {
  TbaTransaction,
  Currency,
  UncontrolledTransaction,
  Tba,
  TbaParameter,
  Message,
  TbaUncontrolledTransactionsPayload
} from '../../../shared/models';
import { UncontrolledParameter } from '../../../shared/models/uncontrolled-parameter';

@Component({
  selector: 'add-uncontrolled-transactions-modal',
  templateUrl: './add-uncontrolled-transactions-modal.component.html',
})
export class AddUncontrolledTransactionsModalComponent extends ClosableModal implements OnInit, OnDestroy {
  private subscriptions = [];

  data: Tba;
  transactions: TbaTransaction[] = [];
  parameters: UncontrolledParameter[] = [];
  upeCurrency: Currency;
  upeCurrencyScale: number;
  selectedTransaction: UncontrolledTransaction;
  errors: string[] = [];

  inputOldValue = {
    parameterValue: '',
  };

  currentPage = 1;
  totalItems: number;
  selectedPageSize = PaginationConstants.DEFAULT_PAGE_SIZE;
  pageSizes: number[];

  constructor(
    protected modalService: ModalService,
    private _tbaService: TbasService,
    private _sessionService: SessionService,
    private _utilsService: UtilsService,
    private _notificationsService: NotificationService,
    private _numberFormatService: NumberFormatService
  ) {
    super(modalService);
  }

  ngOnInit(): void {
    this.getSuitableTransactions();
    this.getParameters();
    this.listenForErrorMessages();

    this.upeCurrency = this._sessionService.getContainerUpeCurrency();
    this.upeCurrencyScale = this._sessionService.getContainerUpeCurrencyScale();
    this.pageSizes = PaginationConstants.PAGE_SIZES;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  // override
  close(): void {
    if (this.selectedTransaction) {
      this.modalService.openModal({
        component: ConfirmationModalComponent,
        data: {
          title: 'Close Add Transactions',
          content: 'Are you sure you want to cancel your actions and close?',
        } as Message,
        success: () => {
          this.modalService.closeModal(true);
        },
      });

      return;
    }

    this.modalService.closeModal(true);
  }

  getFormatedDate(transaction: UncontrolledTransaction): string {
    return this._utilsService.getFormatedTransactionDate(transaction);
  }

  pageChanged(event: any): void {
    this.currentPage = event.page;
    this.getSuitableTransactions();
  }

  selectedTransactionChange(transaction: UncontrolledTransaction, form = null): void {

    if (!transaction.units && this.data.tbaEvaluationMethod.tbaEvaluationMethodId === TbaEvaluationMethodEnum.CUTCUP &&
      this.data.transactionType.transactionTypeId === TransactionTypeEnum.TangibleGoods
    ) {
      this.modalService.openModal({
        component: MessageModalComponent,
        data: {
          title: 'Error',
          content: 'This uncontrolled transaction cannot be added as it does not contain units!',
        } as Message,
        success: () => {
          form.reset();
        },
      });
    }
  }

  save(closeModal = true): void {
    if (this.isSaveDisabled) {
      return;
    }

    this.isSaveDisabled = true;

    this.errors = [];
    const payload = this.getPayloadForSaving();

    this._tbaService.saveUncontrolled(this.data.tbaId, payload).subscribe(() => {
      if (closeModal) {
        this.modalService.closeModal(true);
        this.success();

        return;
      }

      this.isSaveDisabled = false;
      this.success();
      this.resetForm();
    });
  }

  onParameterValueChanged(event: string, element, field, parameter: UncontrolledParameter, tbaParameterDescription: string): void {
    const twoDecimalPrices = { [TbaConstants.TBA_PARAMETERS_COGS_RELATED]: true, [TbaConstants.TBA_PARAMETERS_COGS_UNRELATED]: true, [TbaConstants.TBA_PARAMETERS_RESALE_PRICE]: true, [TbaConstants.TBA_PARAMETERS_SALES_PRICE]: true };
    twoDecimalPrices[tbaParameterDescription] ? this.formatNumberInput(event, element, field, parameter, 2) : this.formatNumberInput(event, element, field, parameter, 4);
    this.errors = [];
  }

  // setting temp value for formatted input field
  setOldValue(event, field): void {
    this.inputOldValue[field] = event.target.value;
  }

  getFormattedNumber(value: number, maxFractionDigits = 1): string {
    return this._numberFormatService.getFormatedNumber(value, maxFractionDigits);
  }

  resetForm(): void {
    this.getSuitableTransactions();
    this.getParameters();
  }

  getAdditionalParamLabel(parameter: TbaParameter): string {
    if (Object.values(TbaParameterAmountEnum).includes(parameter.tbaParameterId)) {
      return `(${this.upeCurrency.isoCode}${this.getCurrencyScaleString()})`;
    }

    return '';
  }

  private getCurrencyScaleString(): string {
    return this._utilsService.getCurrencyScaleString(this.upeCurrencyScale);
  }

  private getPayloadForSaving(): TbaUncontrolledTransactionsPayload {
    const payload = new TbaUncontrolledTransactionsPayload();

    payload.transactionId = this.selectedTransaction ? this.selectedTransaction.transactionId : null;
    payload.tbaUncontrolledParameters = this.parameters.map((parameter: UncontrolledParameter) => {
      if (!parameter.parameterValue && parameter.tbaParameter.optional) {
        parameter.parameterValue = 0;
      }
      parameter.uncontrolledTransaction = this.selectedTransaction;

      return parameter;
    });

    return payload;
  }

  private getParameters(): void {
    this._tbaService.getAllowedParameters(this.data.tbaId, false).subscribe((res) => {
      this.parameters = res.result.parameters.map((parameter: TbaParameter) => {
        const controlledParameter: UncontrolledParameter = new UncontrolledParameter();

        controlledParameter.tbaParameter = parameter;
        controlledParameter.tba = new Tba();
        controlledParameter.tba.tbaId = this.data.tbaId;

        return controlledParameter;
      });
    });
  }

  public getSuitableTransactions(): void {
    const queryString = this._utilsService.getPaginationQueryString(this.selectedPageSize, this.currentPage);

    this._tbaService.listSuitableUcTransactions(this.data.tbaId, `?${queryString}`).subscribe((res) => {
      this.transactions = res.result.uncontrolledTransactions;
      this.totalItems = res.result.count;

      this.setTransactionData();
    });
  }

  private setTransactionData(): void {
    // UC transaction can have uncontrolled and controlled source and destination entity
    // in order to show it and have a tidy template, we put the one that is sent in the parameter that we show
    this.transactions.forEach((transaction: any) => {
      transaction.source = transaction.ucSourceLegalEntity || transaction.sourceLegalEntity;
      transaction.destination = transaction.ucDestinationLegalEntity || transaction.destinationLegalEntity;
    });

    this._utilsService.setDatesForTransactions(this.transactions);
  }

  private formatNumberInput(
    event: any,
    element: any, // HTML element passed for manipulating cursor location
    field: string,
    parameter: UncontrolledParameter,
    maxFractionDigits = 1): void {

    const validInputRegex = /^[-]?[0-9,]*[.]?[0-9]*$/g;
    const firstChar = event.target.value.slice(0, 1);
    const startsWithDotOrComma = firstChar.includes('.') || firstChar.includes(',');
    const startsWithMinus = firstChar === '-';

    if (!validInputRegex.test(event.target.value)) {
      event.target.value = this.inputOldValue[field];

      return;
    }

    parameter[field] = this._numberFormatService.handleCursorPosition(
      event,
      element,
      parameter[field],
      maxFractionDigits,
      startsWithDotOrComma,
      startsWithMinus
    );
  }

  private listenForErrorMessages(): void {
    this.subscriptions.push(
      this._notificationsService.notification$.subscribe((message) => {
        this.handleErrors(message);
      })
    );
  }

  private handleErrors(res: HttpErrorResponse): void {
    this.isSaveDisabled = false;

    if (res.status === 400) {
      // this.errors.push(res.error.errors.global);
    } else if (res.status === 422) {
      const parameterErrors = Object.keys(res.error.errors).filter(key => key.includes('parameterValue'));

      const transactionIdErrors = Object.keys(res.error.errors).filter(key =>key.includes('transactionId'));
      if (parameterErrors.length > 0) {
        this.errors.push('One or more parameter values are not valid!');
      }

      if (transactionIdErrors.length > 0) {
        this.errors.push(`The transaction hasn't been selected!`);
      }
    } else {
      // this.errors.push(TranslationConstants.GLOBAL_SERVER_ERROR);
    }
  }
}
